/*
 * Menu v5 (#1894): desktop panels, phone drill-down, theme editor preview.
 *
 * The editor re-appends section scripts on every shopify:section:load, so this file is
 * an IIFE, each define is guarded, and every document listener is bound through an
 * AbortController that disconnectedCallback aborts.
 *
 * Tracking: this file only reports what happened, as bubbling menu-v5:* events. It never
 * decides what counts as a visit; ga4-custom-events does.
 *   region-enter / region-leave   pointer (mouse) entered or left the desktop nav
 *   tab-enter                     pointer (mouse) entered a desktop tab: { dept }
 *   panel-shown                   { dept, surface, trigger: hover|click|tap|keyboard|editor }
 *   panel-pinned                  a hover-opened panel's ▾ was clicked: { dept, surface, trigger }
 *   panel-hidden                  { dept, surface, reason }
 *   screen-shown / screen-hidden  phone drill screen: { dept, surface }
 *   link                          { dept, section, position, label, url, surface, button }
 */
(() => {
  if (customElements.get('menu-v5-nav') && customElements.get('menu-v5-drawer')) return;

  const OPEN_DELAY = 200;
  const CLOSE_DELAY = 400;
  const WARM_DELAY = 100;
  const DESKTOP = window.matchMedia('(min-width: 1024px)');

  let pageLoaded = document.readyState === 'complete';
  if (!pageLoaded) window.addEventListener('load', () => (pageLoaded = true), { once: true });

  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const emit = (target, name, detail) =>
    target.dispatchEvent(new CustomEvent(`menu-v5:${name}`, { bubbles: true, detail }));

  const warmImages = (container) => {
    if (!container || !pageLoaded) return;
    container.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  };

  const FOCUSABLE =
    'a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, summary, iframe, [tabindex]';
  const UNRENDERED = new Set(['script', 'style', 'link', 'meta', 'template', 'noscript']);

  const isShown = (element) =>
    typeof element.checkVisibility === 'function'
      ? element.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true })
      : element.getClientRects().length > 0;

  const pointerTrigger = (event, pointerType) => {
    if (event.detail === 0) return 'keyboard';
    return pointerType === 'touch' || pointerType === 'pen' ? 'tap' : 'click';
  };

  // The panel stylesheet loads without blocking render. An open before it arrives waits
  // for it, and opens anyway if it fails. A finished request with media still "print"
  // means it already failed (no event will come), and the timeout covers a failure that
  // left no resource entry. The promise is cached on the <link>, which the editor replaces
  // on every header re-render.
  const STYLES_TIMEOUT = 3000;
  const stylesReady = () => {
    const link = document.getElementById('MenuV5Styles');
    if (!link || link.media === 'all') return null;
    if (performance.getEntriesByName(link.href).length) return null;
    if (!link.menuV5Ready) {
      link.menuV5Ready = new Promise((resolve) => {
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, STYLES_TIMEOUT);
      });
    }
    return link.menuV5Ready;
  };

  const fromMarkup = (link) => ({
    dept: link.dataset.menuDept,
    section: link.dataset.menuSection,
    position: Number(link.dataset.menuPosition) || 0,
  });

  const reportLinks = (element, surface, signal, describe = fromMarkup) => {
    const report = (event, button) => {
      const link = event.target.closest('a[href]');
      if (!link || !element.contains(link)) return;
      emit(element, 'link', {
        ...describe(link),
        label: link.textContent.replace(/\s+/g, ' ').trim(),
        url: link.href,
        surface,
        button,
      });
    };
    element.addEventListener('click', (event) => report(event, 0), { capture: true, signal });
    element.addEventListener(
      'auxclick',
      (event) => {
        if (event.button === 1) report(event, 1);
      },
      { capture: true, signal }
    );
  };

  class MenuV5Nav extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.header = this.closest('header');
      this.nav = this.querySelector('nav');
      this.openTab = null;
      this.openedBy = null;
      this.pending = null;
      this.selectedBlock = null;
      this.pointerInNav = false;
      this.suppressHover = false;

      this.querySelectorAll('.menu-v5__tab').forEach((tab) => {
        const toggle = tab.querySelector('.menu-v5__toggle');
        if (!toggle) return;
        tab.addEventListener('pointerenter', (event) => this.onTabEnter(event, tab), { signal });
        tab.addEventListener('pointerleave', (event) => this.onTabLeave(event, tab), { signal });
        tab.addEventListener('focusout', (event) => this.onFocusOut(event, tab), { signal });
        toggle.addEventListener(
          'pointerdown',
          (event) => {
            this.lastPointerType = event.pointerType;
            this.pressedToggle = toggle;
          },
          { signal }
        );
        toggle.addEventListener('pointerup', () => (this.pressedToggle = null), { signal });
        toggle.addEventListener('pointercancel', () => (this.pressedToggle = null), { signal });
        toggle.addEventListener('click', (event) => this.onToggle(event, tab), { signal });
        toggle.addEventListener('focus', () => warmImages(tab), { signal });
      });

      this.nav.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.pointerInNav = true;
          emit(this, 'region-enter', { surface: 'desktop_panel' });
        },
        { signal }
      );
      this.nav.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.pointerInNav = false;
          this.suppressHover = false;
          emit(this, 'region-leave', { surface: 'desktop_panel' });
        },
        { signal }
      );

      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { capture: true, signal });
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      DESKTOP.addEventListener('change', () => this.onBreakpoint(), { signal });
      // A page restored from the back/forward cache comes back with the panel the shopper
      // left through still open.
      window.addEventListener(
        'pageshow',
        (event) => {
          if (event.persisted) this.close('restore');
        },
        { signal }
      );
      reportLinks(this, 'desktop_panel', signal);

      if (designMode()) {
        document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
        document.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), { signal });
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.warmTimer);
      document.body.classList.remove('menu-v5-open');
    }

    onTabEnter(event, tab) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.closeTimer);
      clearTimeout(this.warmTimer);
      this.warmTimer = setTimeout(() => warmImages(tab), WARM_DELAY);
      emit(tab, 'tab-enter', { dept: tab.dataset.menuDept, surface: 'desktop_panel' });

      // Hover never replaces a panel opened by click or keyboard. Gating on focus instead
      // would differ by browser: Safari and Firefox on macOS don't focus a clicked button.
      if (this.suppressHover || this.openTab === tab) return;
      if (this.openTab && this.openedBy !== 'hover') return;

      clearTimeout(this.openTimer);
      if (this.openTab && this.openedBy === 'hover') {
        this.open(tab, 'hover');
      } else {
        this.openTimer = setTimeout(() => this.open(tab, 'hover'), OPEN_DELAY);
      }
    }

    onTabLeave(event, tab) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.openTimer);
      clearTimeout(this.warmTimer);
      if (this.pending && this.pending.tab === tab && this.pending.trigger === 'hover') this.pending = null;
      if (this.openTab === tab && this.openedBy === 'hover') {
        this.closeTimer = setTimeout(() => this.close('mouseleave'), CLOSE_DELAY);
      }
    }

    onToggle(event, tab) {
      event.preventDefault();
      const trigger = pointerTrigger(event, this.lastPointerType);
      this.lastPointerType = null;
      clearTimeout(this.openTimer);

      if (this.openTab === tab) {
        if (this.openedBy === 'hover') {
          clearTimeout(this.closeTimer);
          this.openedBy = trigger;
          emit(tab, 'panel-pinned', { dept: tab.dataset.menuDept, surface: 'desktop_panel', trigger });
        } else {
          this.close('toggle');
        }
        return;
      }
      this.open(tab, trigger);
    }

    // Chrome focuses a button on mousedown, so pressing another tab's toggle moves focus out
    // of this tab before its click. That click switches panels; it isn't a blur.
    onFocusOut(event, tab) {
      if (this.openTab !== tab) return;
      const next = event.relatedTarget;
      if (next && next === this.pressedToggle) return;
      if (next && !tab.contains(next)) this.close('blur');
    }

    // The header doesn't stick, so an open panel scrolls away with it while the overlay
    // stays over the page. Once the tab bar is off screen, the panel closes.
    onScroll() {
      if (!this.openTab) return;
      const anchor = this.closest('.header__bottom');
      if (anchor && anchor.getBoundingClientRect().bottom <= 0) this.close('scroll');
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      clearTimeout(this.openTimer);
      this.pending = null;
      const tab = this.openTab;
      if (!tab) return;
      const hadFocus = tab.contains(document.activeElement);
      this.close('esc');
      if (hadFocus) tab.querySelector('.menu-v5__toggle').focus();
      this.suppressHover = this.pointerInNav;
    }

    onDocumentClick(event) {
      if (this.openTab && !this.contains(event.target)) this.close('outside_click');
    }

    // Crossing 1024px (an iPad rotating, a window resizing) closes the panel, which the
    // other layout can't show. The drawer closes itself. In the editor, a selected block's
    // preview moves to the layout now showing.
    onBreakpoint() {
      clearTimeout(this.openTimer);
      if (!designMode()) {
        this.close('resize');
        return;
      }
      this.close('editor');
      if (this.drawer) this.drawer.hideForEditor();
      const tab = this.selectedBlock && this.querySelector(`[data-menu-v5-block="${CSS.escape(this.selectedBlock)}"]`);
      if (tab) this.previewBlock(tab, this.selectedBlock, false);
    }

    open(tab, trigger, animate = true) {
      const waiting = stylesReady();
      if (waiting) {
        this.pending = { tab, trigger };
        waiting.then(() => {
          if (this.pending && this.pending.tab === tab) {
            this.pending = null;
            this.show(tab, trigger, animate);
          }
        });
        return;
      }
      this.show(tab, trigger, animate);
    }

    show(tab, trigger, animate) {
      if (this.openTab === tab) return;
      const switching = Boolean(this.openTab);
      if (switching) this.close('switch');
      clearTimeout(this.closeTimer);

      // Panels hang off .header__bottom; their max-height keeps the bottom on screen.
      const anchor = this.closest('.header__bottom');
      if (anchor) {
        const top = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
        this.style.setProperty('--menu-v5-panel-top', `${top}px`);
      }

      const panel = tab.querySelector('.menu-v5__panel');
      // Only the first open fades in; a switch swaps panels at once so the page never shows through between them.
      panel.toggleAttribute('data-no-transition', !animate || switching);
      panel.hidden = false;
      tab.querySelector('.menu-v5__toggle').setAttribute('aria-expanded', 'true');
      tab.classList.add('is-open');
      document.body.classList.add('menu-v5-open');
      this.openTab = tab;
      this.openedBy = trigger;
      emit(tab, 'panel-shown', { dept: tab.dataset.menuDept, surface: 'desktop_panel', trigger });
    }

    close(reason) {
      this.pending = null;
      clearTimeout(this.closeTimer);
      const tab = this.openTab;
      if (!tab) return;

      tab.querySelector('.menu-v5__panel').hidden = true;
      tab.querySelector('.menu-v5__toggle').setAttribute('aria-expanded', 'false');
      tab.classList.remove('is-open');
      if (reason !== 'switch') document.body.classList.remove('menu-v5-open');
      this.openTab = null;
      this.openedBy = null;
      emit(tab, 'panel-hidden', { dept: tab.dataset.menuDept, surface: 'desktop_panel', reason });
    }

    get drawer() {
      return this.header && this.header.querySelector('menu-v5-drawer');
    }

    // With the switch on legacy, the editor still renders v5 (hidden) so merch can see
    // their blocks: selecting one shows v5 in place of legacy until it's deselected.
    setPreview(on) {
      if (!this.hasAttribute('data-menu-v5-preview-only')) return;
      this.hidden = !on;
      if (this.drawer) this.drawer.hidden = !on;
      if (this.header) this.header.toggleAttribute('data-menu-v5-preview', on);
    }

    onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId;
      const tab = blockId && this.querySelector(`[data-menu-v5-block="${CSS.escape(blockId)}"]`);
      if (!tab) {
        this.selectedBlock = null;
        this.setPreview(false);
        return;
      }
      this.selectedBlock = blockId;
      this.setPreview(true);
      this.previewBlock(tab, blockId, !event.detail.load);
    }

    previewBlock(tab, blockId, animate) {
      if (DESKTOP.matches) {
        if (tab.querySelector('.menu-v5__panel')) this.show(tab, 'editor', animate);
      } else if (this.drawer) {
        this.drawer.showBlock(blockId, animate);
      }
    }

    onBlockDeselect(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId || !this.querySelector(`[data-menu-v5-block="${CSS.escape(blockId)}"]`)) return;
      this.selectedBlock = null;
      this.close('editor');
      if (this.drawer) this.drawer.hideForEditor();
      this.setPreview(false);
    }
  }

  class MenuV5Drawer extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.menuDrawer = this.closest('menu-drawer');
      this.root = this.querySelector('[data-menu-v5-screen="root"]');
      this.current = null;
      this.lastRow = null;
      this.closeReason = null;
      this.openTrigger = null;
      this.trapping = false;
      this.inerted = [];
      this.yielding = false;
      this.yieldTo = null;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      reportLinks(this, 'phone_drawer', signal);
      this.querySelectorAll('[data-menu-v5-drill]').forEach((row) => {
        row.addEventListener('touchstart', () => warmImages(this.screenFor(row)), { passive: true, signal });
      });

      // menu-drawer:open fires before the drawer traps focus; handleAfterHide is skipped
      // when the drawer reopens mid-close, so both reset to the root screen.
      document.addEventListener('menu-drawer:open', () => this.reset(), { signal });
      if (!this.menuDrawer) return;
      this.menuDrawer.addEventListener('drawer:handleAfterHide', () => this.onAfterHide(), { signal });
      this.menuDrawer.addEventListener('drawer:handleAfterShow', () => this.onAfterShow(), { signal });
      this.menuDrawer.addEventListener(
        'toggle',
        (event) => {
          if (event.target === this.menuDrawer) this.onDrawerToggle();
        },
        { signal }
      );
      document.addEventListener('toggle', (event) => this.onModalToggle(event), { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { capture: true, signal });
      // The drawer is phone-only: crossing 1024px would leave it open over the desktop
      // layout with the page inert. MenuV5Nav handles the editor.
      DESKTOP.addEventListener(
        'change',
        () => {
          if (!designMode()) this.closeFor('resize');
        },
        { signal }
      );
      window.addEventListener(
        'pageshow',
        (event) => {
          if (event.persisted) this.closeFor('restore');
        },
        { signal }
      );

      // The drawer footer (account, custom link, social icons) is shared with legacy and
      // sits outside this element, so its links are numbered here in the order shown.
      const footer = this.menuDrawer.querySelector('.menu-drawer__footer');
      if (footer) {
        reportLinks(footer, 'phone_drawer', signal, (link) => ({
          dept: 'root',
          section: 'drawer_footer',
          position: Array.from(footer.querySelectorAll('a[href]')).indexOf(link) + 1,
        }));
      }

      // Close and open intents are recorded in capture listeners, before the drawer
      // reacts, so panel-hidden can say how it closed.
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (event.target.closest('.menu-drawer-button')) this.openPointerType = event.pointerType;
        },
        { capture: true, signal }
      );
      document.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('.menu-drawer-button')) {
            if (this.menuDrawer.open) this.closeReason = 'close_button';
            else this.openTrigger = pointerTrigger(event, this.openPointerType);
          } else if (this.menuDrawer.open && event.target.closest('menu-drawer > .fixed-overlay')) {
            this.closeReason = 'overlay';
          } else if (this.trapping) {
            this.onHeaderClick(event);
          }
        },
        { capture: true, signal }
      );
      document.addEventListener(
        'keyup',
        (event) => {
          if (event.code === 'Escape' && this.menuDrawer.open) this.closeReason = 'esc';
        },
        { capture: true, signal }
      );
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.release();
    }

    screenFor(row) {
      return this.querySelector(`#${CSS.escape(row.dataset.menuV5Drill)}`);
    }

    get menuButton() {
      if (!this.menuDrawer || !this.menuDrawer.id) return null;
      return document.querySelector(`.menu-drawer-button[aria-controls="${CSS.escape(this.menuDrawer.id)}"]`);
    }

    // The header menu button is the drawer's only close control (a disclosure button):
    // ModalComponent keeps its aria-expanded in step, and this swaps its label.
    onDrawerToggle() {
      if (this.hidden) return;
      const { open } = this.menuDrawer;
      if (open) {
        emit(this, 'panel-shown', { dept: 'root', surface: 'phone_drawer', trigger: this.openTrigger || 'tap' });
      } else {
        const dept = this.current ? this.current.dataset.menuDept : 'root';
        emit(this, 'panel-hidden', { dept, surface: 'phone_drawer', reason: this.closeReason || 'other' });
      }
      this.openTrigger = null;
      this.closeReason = null;

      const button = this.menuButton;
      const label = button && button.getAttribute(open ? 'data-menu-v5-label-close' : 'data-menu-v5-label-open');
      if (label) button.setAttribute('aria-label', label);

      if (designMode()) return;
      if (open) {
        this.yielding = false;
        this.yieldTo = null;
        this.contain();
        return;
      }
      this.release();
      if (this.yielding) {
        this.yielding = false;
        return;
      }
      const active = document.activeElement;
      if (button && (!active || active === document.body || this.menuDrawer.contains(active))) {
        button.focus({ preventScroll: true });
      }
    }

    // A click (or keyboard activation, which clicks) on another header control, like the
    // cart or wishlist button, or on a header link that navigates, closes the menu before
    // the control acts, so what it opens isn't left inside the inert page.
    onHeaderClick(event) {
      const header = this.menuDrawer.closest('header');
      const { target } = event;
      if (!header || !header.contains(target) || this.menuDrawer.contains(target)) return;
      const control = target.closest('[aria-controls]');
      if (control && header.contains(control)) {
        const id = control.getAttribute('aria-controls');
        if (id !== this.menuDrawer.id) this.yieldMenu('outside_click', document.getElementById(id));
        return;
      }
      const link = target.closest('a[href]');
      const href = link && header.contains(link) ? link.getAttribute('href') : '';
      if (href && !href.startsWith('#') && !/^javascript:/i.test(href)) this.yieldMenu('outside_click', null);
    }

    // Any other modal opening (the cart drawer after an add, say) closes the menu too.
    onModalToggle(event) {
      const Modal = customElements.get('modal-component');
      const modal = event.target;
      if (!this.trapping || modal === this.menuDrawer || !Modal || !(modal instanceof Modal) || !modal.open) return;
      this.yieldMenu(null, modal);
    }

    closeFor(reason) {
      if (!this.menuDrawer.open) return;
      this.closeReason = reason;
      this.menuDrawer.hide();
    }

    // Closes the menu for something else. ModalComponent would hand focus back to the menu
    // button after the hide, so its activeElement is cleared: focus belongs to the other side.
    yieldMenu(reason, other) {
      if (!this.menuDrawer.open) return;
      if (reason) this.closeReason = reason;
      this.yielding = true;
      this.yieldTo = other || null;
      this.menuDrawer.activeElement = null;
      this.menuDrawer.hide();
    }

    // handleAfterHide queues a removeTrapFocus, which drops whichever trap is current. When
    // the menu closed for another modal, that can be the other modal's, so it's set again.
    onAfterHide() {
      this.reset();
      const other = this.yieldTo;
      this.yieldTo = null;
      if (!other || designMode() || !window.FoxTheme) return;
      setTimeout(() => {
        if (other.open) window.FoxTheme.a11y.trapFocus(other, null);
      });
    }

    // ModalComponent's shared trap only cycles inside <menu-drawer>, which would shut the
    // header button out, so it's dropped as soon as it's set and onKeydown runs the loop.
    onAfterShow() {
      if (this.hidden || designMode() || !this.menuDrawer.open) return;
      if (window.FoxTheme) window.FoxTheme.a11y.removeTrapFocus();
      if (!this.current) {
        const first = this.root.querySelector('.menu-v5-row');
        if (first) first.focus({ preventScroll: true });
      }
    }

    // While open, everything outside the header is inert. The rest of the header stays
    // live (the menu button is in it) but onKeydown keeps Tab off it.
    contain() {
      if (this.trapping) return;
      this.trapping = true;
      const start = this.menuDrawer.closest('header') || this.menuDrawer;
      for (let node = start; node !== document.body && node.parentElement; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling === node || sibling.inert || UNRENDERED.has(sibling.localName)) continue;
          sibling.inert = true;
          this.inerted.push(sibling);
        }
      }
    }

    release() {
      this.trapping = false;
      this.inerted.forEach((element) => (element.inert = false));
      this.inerted = [];
    }

    tabStops() {
      const reachable = (element) => element.tabIndex >= 0 && !element.closest('[hidden], [inert]') && isShown(element);
      const stops = Array.from(this.menuDrawer.querySelectorAll(FOCUSABLE)).filter(reachable);
      const button = this.menuButton;
      return button && reachable(button) ? [button, ...stops] : stops;
    }

    // Tab and Shift+Tab cycle through the header menu button, then the showing screen and
    // the drawer footer. Focus on something outside that list (the page, or a spot a click
    // left it) moves to the nearest stop in the direction of travel.
    onKeydown(event) {
      if (!this.trapping || event.key !== 'Tab' || event.ctrlKey || event.metaKey) return;
      const stops = this.tabStops();
      if (!stops.length) return;
      const active = document.activeElement || document.body;
      const index = stops.indexOf(active);
      let next;
      if (index === -1) {
        const position = (element) => active.compareDocumentPosition(element);
        next = event.shiftKey
          ? stops.filter((element) => position(element) & Node.DOCUMENT_POSITION_PRECEDING).pop()
          : stops.find((element) => position(element) & Node.DOCUMENT_POSITION_FOLLOWING);
        if (!next) next = event.shiftKey ? stops[stops.length - 1] : stops[0];
      } else {
        next = stops[(index + (event.shiftKey ? stops.length - 1 : 1)) % stops.length];
      }
      event.preventDefault();
      next.focus();
    }

    onClick(event) {
      const drillRow = event.target.closest('[data-menu-v5-drill]');
      if (drillRow) {
        this.drill(drillRow);
      } else if (event.target.closest('[data-menu-v5-up]')) {
        this.up();
      }
    }

    drill(row, animate = true, focus = true) {
      const screen = this.screenFor(row);
      if (!screen) return;
      if (this.current && this.current !== screen) this.current.hidden = true;
      this.lastRow = row;
      this.root.hidden = true;
      this.root.inert = true;
      screen.toggleAttribute('data-no-transition', !animate);
      screen.inert = false;
      screen.hidden = false;
      const scroller = this.closest('.menu-drawer__menus');
      if (scroller) {
        // Back returns to the row the shopper drilled from, so keep the root list's place.
        if (!this.current) this.rootScroll = scroller.scrollTop;
        scroller.scrollTop = 0;
      }
      this.current = screen;
      const title = screen.querySelector('.menu-v5-screen__title');
      if (focus && title) title.focus({ preventScroll: true });
      emit(this, 'screen-shown', { dept: screen.dataset.menuDept, surface: 'phone_drawer' });
    }

    up() {
      const screen = this.current;
      if (!screen) return;
      screen.hidden = true;
      this.root.inert = false;
      this.root.hidden = false;
      this.current = null;
      const scroller = this.closest('.menu-drawer__menus');
      if (scroller) scroller.scrollTop = this.rootScroll || 0;
      if (this.lastRow) this.lastRow.focus({ preventScroll: true });
      emit(this, 'screen-hidden', { dept: screen.dataset.menuDept, surface: 'phone_drawer' });
    }

    reset() {
      this.querySelectorAll('[data-menu-v5-screen]:not([data-menu-v5-screen="root"])').forEach((screen) => {
        screen.hidden = true;
      });
      this.root.inert = false;
      this.root.hidden = false;
      this.current = null;
      this.rootScroll = 0;
    }

    showBlock(blockId, animate) {
      const row = this.querySelector(`[data-menu-v5-drill="MenuScreen-${CSS.escape(blockId)}"]`);
      const drillIn = () => {
        if (row) this.drill(row, animate, false);
      };
      if (this.menuDrawer && !this.menuDrawer.open) {
        Promise.resolve(this.menuDrawer.show(null, animate)).then(drillIn);
      } else {
        drillIn();
      }
    }

    hideForEditor() {
      if (this.menuDrawer && this.menuDrawer.open) this.menuDrawer.hide();
    }
  }

  if (!customElements.get('menu-v5-nav')) customElements.define('menu-v5-nav', MenuV5Nav);
  if (!customElements.get('menu-v5-drawer')) customElements.define('menu-v5-drawer', MenuV5Drawer);
})();
